import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle, FileText, Loader2, Plus } from 'lucide-react'
import type { LicenseCriteria, Organisation, XeroInvoiceState } from '../../types'
import type { CriteriaScenario } from './use-license-criteria-form'
import type { AccessTierOption, FeatureOption } from './license-criteria-form-lookups'

const INPUT_CLASS =
  'w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-lg bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100'
const LABEL_CLASS = 'block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1'

export function FormHeader(): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <>
      <h3 className="mb-4 text-lg font-semibold text-stone-900 dark:text-white">
        {t('licensing.createCriteria')}
      </h3>
      <p className="mb-4 text-sm text-stone-600 dark:text-stone-400">
        {t('licensing.manualEntryDescription')}
      </p>
    </>
  )
}

export function ScenarioAlerts({
  scenario,
  hasSelectedInvoice,
  orphanedInvoicesCount,
  selectedInvoice,
}: {
  scenario: CriteriaScenario
  hasSelectedInvoice: boolean
  orphanedInvoicesCount: number
  selectedInvoice: XeroInvoiceState | undefined
}): ReactElement | null {
  const { t } = useTranslation('admin')
  if (scenario === 'orphaned_invoices_available' && !hasSelectedInvoice) {
    return (
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-cyan-200 bg-cyan-50 p-4 dark:border-cyan-800 dark:bg-cyan-900/20">
        <FileText className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600 dark:text-cyan-400" />
        <div>
          <p className="text-sm font-medium text-cyan-800 dark:text-cyan-200">
            {t('licensing.orphanedInvoicesAvailable')}
          </p>
          <p className="text-sm text-cyan-700 dark:text-cyan-300">
            {t('licensing.orphanedInvoicesDetail', { count: orphanedInvoicesCount })}
          </p>
        </div>
      </div>
    )
  }
  if (scenario === 'invoice_without_org' && selectedInvoice) {
    return (
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-cyan-200 bg-cyan-50 p-4 dark:border-cyan-800 dark:bg-cyan-900/20">
        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600 dark:text-cyan-400" />
        <div>
          <p className="text-sm font-medium text-cyan-800 dark:text-cyan-200">
            {t('licensing.creatingFromOrphanedInvoice')}
          </p>
          <p className="text-sm text-cyan-700 dark:text-cyan-300">
            {t('licensing.orphanedInvoiceLinkDescription', {
              invoiceNumber: selectedInvoice.xeroInvoiceNumber,
            })}
          </p>
        </div>
      </div>
    )
  }
  return null
}

export function OrgSelector({
  organisations,
  organisationId,
  hasOrphanedInvoices,
  hasSelectedInvoice,
  onChange,
}: {
  organisations: Organisation[]
  organisationId: string
  hasOrphanedInvoices: boolean
  hasSelectedInvoice: boolean
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className={LABEL_CLASS}>
        {t('licensing.organisation')} {!hasOrphanedInvoices && '*'}
      </label>
      <select
        value={organisationId}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={INPUT_CLASS}
        required={!hasOrphanedInvoices && !hasSelectedInvoice}
      >
        <option value="">
          {hasOrphanedInvoices
            ? t('licensing.selectOrgOrInvoice')
            : t('licensing.selectOrganisation')}
        </option>
        {organisations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name} {org.xeroOrganisationId && t('licensing.xeroLinked')}
          </option>
        ))}
      </select>
    </div>
  )
}

export function InvoiceDropdown({
  availableInvoices,
  selectedInvoiceId,
  hasOrgSelected,
  onSelect,
  onSwitchToCustom,
}: {
  availableInvoices: XeroInvoiceState[]
  selectedInvoiceId: string
  hasOrgSelected: boolean
  onSelect: (id: string) => void
  onSwitchToCustom: () => void
}): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-2">
      <select
        value={selectedInvoiceId}
        onChange={(e) => {
          onSelect(e.target.value)
        }}
        className={INPUT_CLASS}
        required={!hasOrgSelected}
      >
        {(availableInvoices.length > 1 || hasOrgSelected) && (
          <option value="">
            {hasOrgSelected ? t('licensing.selectInvoiceOptional') : t('licensing.selectInvoice')}
          </option>
        )}
        {availableInvoices.map((inv) => (
          <option key={inv.id} value={inv.id}>
            {inv.xeroInvoiceNumber} - {inv.currencyCode} {inv.amountPaid.toFixed(2)} (
            {inv.invoiceDate ?? t('licensing.noDate')})
          </option>
        ))}
      </select>
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-[#5c6e94] dark:text-[#8a9bba]" />
        <span className="text-xs text-[#5c6e94] dark:text-[#8a9bba]">
          {t('licensing.invoicesAvailable', {
            count: availableInvoices.length,
            type: hasOrgSelected ? t('licensing.unlinked') : t('licensing.orphaned'),
          })}
        </span>
        {hasOrgSelected && (
          <button
            type="button"
            onClick={onSwitchToCustom}
            className="text-xs text-stone-500 underline hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
          >
            {t('licensing.enterCustomReference')}
          </button>
        )}
      </div>
    </div>
  )
}

export function CustomReferenceInput({
  value,
  hasInvoicesAvailable,
  useCustomReference,
  onChange,
  onSwitchToList,
}: {
  value: string
  hasInvoicesAvailable: boolean
  useCustomReference: boolean
  onChange: (value: string) => void
  onSwitchToList: () => void
}): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={t('licensing.referencePlaceholder')}
        className={`${INPUT_CLASS} placeholder-stone-400`}
      />
      {!hasInvoicesAvailable && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-xs text-amber-700 dark:text-amber-300">
            {t('licensing.noInvoicesWarning')}
          </div>
        </div>
      )}
      {useCustomReference && hasInvoicesAvailable && (
        <button
          type="button"
          onClick={onSwitchToList}
          className="text-xs text-stone-500 underline hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
        >
          {t('licensing.selectFromAvailableInvoices')}
        </button>
      )}
    </div>
  )
}

export function InvoiceOrReferenceField(props: {
  hasOrgSelected: boolean
  availableInvoices: XeroInvoiceState[]
  useCustomReference: boolean
  selectedInvoiceId: string
  externalReference: string
  onSelectInvoice: (id: string) => void
  onSwitchToCustom: () => void
  onChangeReference: (value: string) => void
  onSwitchToList: () => void
}): ReactElement {
  const { t } = useTranslation('admin')
  const {
    hasOrgSelected,
    availableInvoices,
    useCustomReference,
    selectedInvoiceId,
    externalReference,
    onSelectInvoice,
    onSwitchToCustom,
    onChangeReference,
    onSwitchToList,
  } = props
  const showDropdown = availableInvoices.length > 0 && !useCustomReference
  return (
    <div>
      <label className={LABEL_CLASS}>
        {hasOrgSelected ? t('licensing.invoiceOrReference') : t('licensing.selectInvoiceRequired')}
      </label>
      {showDropdown ? (
        <InvoiceDropdown
          availableInvoices={availableInvoices}
          selectedInvoiceId={selectedInvoiceId}
          hasOrgSelected={hasOrgSelected}
          onSelect={onSelectInvoice}
          onSwitchToCustom={onSwitchToCustom}
        />
      ) : hasOrgSelected ? (
        <CustomReferenceInput
          value={externalReference}
          hasInvoicesAvailable={availableInvoices.length > 0}
          useCustomReference={useCustomReference}
          onChange={onChangeReference}
          onSwitchToList={onSwitchToList}
        />
      ) : (
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {t('licensing.noInvoicesAvailable')}
        </p>
      )}
    </div>
  )
}

export function AccessTierField({
  value,
  options,
  onChange,
}: {
  value: string
  options: AccessTierOption[]
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className={LABEL_CLASS}>{t('licensing.accessTier')} *</label>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={INPUT_CLASS}
        required
      >
        {options.map((tier) => (
          <option key={tier.value} value={tier.value}>
            {tier.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function NumericField({
  labelKey,
  value,
  min,
  required,
  onChange,
}: {
  labelKey: 'licensing.seatsGranted' | 'licensing.storageVolume'
  value: number
  min: number
  required?: boolean
  onChange: (value: number) => void
}): ReactElement {
  const { t } = useTranslation('admin')
  const requiredFlag = required ?? false
  return (
    <div>
      <label className={LABEL_CLASS}>
        {t(labelKey)} {requiredFlag && '*'}
      </label>
      <input
        type="number"
        min={String(min)}
        value={value}
        onChange={(e) => {
          onChange(parseInt(e.target.value) || min)
        }}
        className={INPUT_CLASS}
        required={requiredFlag}
      />
    </div>
  )
}

export function DateField({
  labelKey,
  hint,
  value,
  required,
  onChange,
}: {
  labelKey: 'licensing.validFrom' | 'licensing.validUntil'
  hint?: 'licensing.perpetualLicenseHint'
  value: string
  required?: boolean
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('admin')
  const requiredFlag = required ?? false
  return (
    <div>
      <label className={LABEL_CLASS}>
        {t(labelKey)} {requiredFlag && '*'}
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={INPUT_CLASS}
        required={requiredFlag}
      />
      {hint && <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">{t(hint)}</p>}
    </div>
  )
}

interface FeatureCheckboxProps {
  feature: FeatureOption
  isSelected: boolean
  onToggle: (checked: boolean) => void
}

function FeatureCheckbox({ feature, isSelected, onToggle }: FeatureCheckboxProps): ReactElement {
  const containerClass = isSelected
    ? 'bg-[#4e5e82]/10 dark:bg-[#8a9bba]/15 border border-[#8a9bba] dark:border-[#5c6e94]'
    : 'bg-white dark:bg-stone-700 border border-transparent hover:bg-stone-100 dark:hover:bg-stone-600'
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg p-2 transition-colors ${containerClass}`}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => {
          onToggle(e.target.checked)
        }}
        className="mt-1 h-4 w-4 rounded border-stone-300 text-[#5c6e94] focus:ring-[#4e5e82]"
      />
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-stone-900 dark:text-stone-100">
          {feature.label}
        </span>
        <span className="block text-xs text-stone-500 dark:text-stone-400">
          {feature.description}
        </span>
      </div>
    </label>
  )
}

export function FeaturesField({
  options,
  selected,
  onToggle,
}: {
  options: FeatureOption[]
  selected: string[]
  onToggle: (value: string, checked: boolean) => void
}): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
        {t('licensing.featuresGranted')}
      </label>
      <div className="grid grid-cols-1 gap-2 rounded-lg border border-stone-300 bg-stone-50 p-3 sm:grid-cols-2 dark:border-stone-600 dark:bg-stone-700/50">
        {options.map((feature) => (
          <FeatureCheckbox
            key={feature.value}
            feature={feature}
            isSelected={selected.includes(feature.value)}
            onToggle={(checked) => {
              onToggle(feature.value, checked)
            }}
          />
        ))}
      </div>
      {selected.length > 0 && (
        <p className="mt-2 text-xs text-[#5c6e94] dark:text-[#8a9bba]">
          {t('licensing.featuresSelected', { count: selected.length })}
        </p>
      )}
    </div>
  )
}

export function SubmitButton({
  disabled,
  submitting,
}: {
  disabled: boolean
  submitting: boolean
}): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      type="submit"
      disabled={disabled}
      className="flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {submitting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('licensing.creating')}
        </>
      ) : (
        <>
          <Plus className="h-4 w-4" />
          {t('licensing.createCriteria')}
        </>
      )}
    </button>
  )
}

export function ErrorAlert({ error }: { error: string | null }): ReactElement | null {
  if (!error) return null
  return (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
    </div>
  )
}

export function SuccessAlert({
  success,
  scenario,
}: {
  success: LicenseCriteria | null
  scenario: CriteriaScenario
}): ReactElement | null {
  const { t } = useTranslation('admin')
  if (!success) return null
  return (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-[#4e5e82]/15 bg-[#4e5e82]/5 p-4 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10">
      <CheckCircle className="h-5 w-5 shrink-0 text-[#5c6e94] dark:text-[#8a9bba]" />
      <div>
        <p className="font-medium text-[#5c6e94] dark:text-[#8a9bba]">
          {t('licensing.criteriaCreatedSuccess')}
        </p>
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
          {t('licensing.status')}:{' '}
          <span className="font-medium text-amber-600 dark:text-amber-400">
            {t('licensing.pendingVerification')}
          </span>
        </p>
        {scenario === 'org_without_invoice' && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            {t('licensing.noInvoiceLinkedNote')}
          </p>
        )}
        {scenario === 'invoice_without_org' && (
          <p className="mt-1 text-xs text-cyan-600 dark:text-cyan-400">
            {t('licensing.autoLinkNote')}
          </p>
        )}
      </div>
    </div>
  )
}
